import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  Dropdown,
  Option,
  Spinner,
  Button
} from "@fluentui/react-components";
import { DismissCircle20Regular } from "@fluentui/react-icons";
import { getAccessToken } from "../../Services/GraphServices";
import {
  getChatFlowChannel,
  assigneChannel,
  unAssigneChannel,
} from "../../Services/HeedifyServices";
import { useTranslation } from "react-i18next";

export default function ChannelsComponent(props) {
  const { instance, accounts } = useMsal();
  const { t } = useTranslation();
  const activeAccount = accounts[0];
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [agentsOptions, setAgentsOptions] = useState([]);

  // ✅ Build agents options from props.queue.agentDetails
  useEffect(() => {
    if (props.queue?.agentDetails) {
      const agents = Object.entries(props.queue.agentDetails).map(
        ([key, value]) => ({
          key,
          displayName: value.displayName,
        })
      );
      setAgentsOptions(agents);
    } else {
      setAgentsOptions([]);
    }
  }, [props.queue]);

  // ✅ Fetch all channels for each chatFlow
  const getChannels = () => {
    if (!activeAccount) return;

    setLoading(true);

    getAccessToken(instance, activeAccount)
      .then((Token) => {
        const chatFlows = props.chatFlows || [];
        if (chatFlows.length === 0) {
          setLoading(false);
          return;
        }

        const allRequests = chatFlows.map((chatFlow) =>
          getChatFlowChannel(
            props.ACTIVE_TENANT,
            chatFlow.scope,
            chatFlow.id,
            Token
          )
            .then((result) => {
              if (!Array.isArray(result)) return [];
              return result.map((item) => {
                // ✅ Initialize assignedAgent properly
                const assignedAgent =
                  item.assignedAgent && item.assignedAgent.id
                    ? item.assignedAgent
                    : {};

                return {
                  ...item,
                  assignedAgent,
                  chatFlow,
                };
              });
            })
            .catch((err) => {
              console.error("Error in chatFlowChannel:", err);
              return [];
            })
        );

        return Promise.all(allRequests).then((results) => {
          const flattenedChannels = results.flat();
          setChannels(flattenedChannels);
        });
      })
      .catch((err) => console.error("❌ Error fetching channels:", err))
      .finally(() => setLoading(false));
  };

  // ✅ Assign/Unassign agent (local update only)
  const handleAgentChange = (event, data, item, chatFlow) => {
    getAccessToken(instance, activeAccount)
      .then((Token) => {
        const selectedAgentId = data.optionValue;

        if (selectedAgentId) {
          const agent = props.queue.agentDetails[selectedAgentId];
          const payload = {
            agentId: selectedAgentId,
            agentDisplayName: agent.displayName,
            channelId: item.channelId,
            teamName: item.teamName,
            channelName: item.channelName,
            queueId: props.queue.RowKey,
          };

          return assigneChannel(
            props.ACTIVE_TENANT,
            chatFlow.scope,
            chatFlow.id,
            item.channelId,
            Token,
            payload
          ).then(() => ({
            id: selectedAgentId,
            displayName: agent.displayName,
          }));
        } else {
          return unAssigneChannel(
            props.ACTIVE_TENANT,
            chatFlow.scope,
            chatFlow.id,
            item.channelId,
            Token
          ).then(() => ({}));
        }
      })
      .then((newAssignedAgent) => {
        // ✅ Update state locally (no reload)
        setChannels((prevChannels) =>
          prevChannels.map((ch) =>
            ch.channelId === item.channelId
              ? { ...ch, assignedAgent: newAssignedAgent }
              : ch
          )
        );
      })
      .catch((err) => console.error("❌ Error in handleAgentChange:", err));
  };

  // ✅ Initial load when chatFlows are ready
  useEffect(() => {
    if (props.chatFlows?.length > 0) {
      getChannels();
    }
  }, [props.chatFlows]);

  return (
    <div style={{ padding: "20px" }}>
      <h3 style={{ marginBottom: "10px" }}>Channels</h3>

      {loading ? (
        <Spinner label="Loading channels..." />
      ) : (
        <Table aria-label="Channels table">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{t("CHANNEL_NAME")}</TableHeaderCell>
              <TableHeaderCell>{t("TEAMS_NAME")}</TableHeaderCell>
              <TableHeaderCell>{t("ASSIGNED_AGENT")}</TableHeaderCell>
            </TableRow>
          </TableHeader>

          <TableBody>
            {channels.map((item) => (
              <TableRow key={item.channelId}>
                <TableCell>{item.channelName}</TableCell>
                <TableCell>{item.teamName || "-"}</TableCell>
                <TableCell>
                  <div
                    style={{ display: "flex", alignItems: "center", gap: "8px" }}
                  >
                    <Dropdown
                      style={{ flex: 1 }}
                      placeholder={t("SELECT_AGENT")}
                      disabled={!props.permissions?.canUpdate}
                      selectedOptions={
                        item.assignedAgent?.id
                          ? [item.assignedAgent.id]
                          : []
                      }
                      value={
                        item.assignedAgent?.displayName
                          ? item.assignedAgent.displayName
                          : ""
                      }
                      onOptionSelect={(e, data) =>
                        handleAgentChange(e, data, item, item.chatFlow)
                      }
                    >
                      {agentsOptions.map((agent) => (
                        <Option key={agent.key} value={agent.key}>
                          {agent.displayName}
                        </Option>
                      ))}
                    </Dropdown>

                    {/* ❌ Unassign Button */}
                    {item.assignedAgent?.id && (
                      <Button
                        appearance="subtle"
                        icon={<DismissCircle20Regular />}
                        title={t("UNASSIGN_AGENT")}
                        disabled={!props.permissions?.canDelete}
                        onClick={() =>
                          handleAgentChange(
                            null,
                            { optionValue: null },
                            item,
                            item.chatFlow
                          )
                        }
                      />
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
